import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Code,
  GraduationCap,
  Chalkboard,
  Calendar,
  Article,
  PencilSimple,
  Phone,
  MapPin,
  Books,
  Student,
  Envelope,
  DownloadSimple,
  Trophy,
  Star,
} from "@phosphor-icons/react";
import PostCard from "@/components/PostCard";

// Scorecard colour config
const SCORE_TYPES = [
  { key: "referral_marks",   label: "Referral Marks",  subtitle: "50 pts / referral",   color: "#22c55e", bg: "rgba(34,197,94,0.08)",   border: "rgba(34,197,94,0.25)"  },
  { key: "bonus_marks",     label: "Bonus Marks",     subtitle: "Admin assigned",       color: "#38bdf8", bg: "rgba(56,189,248,0.08)",  border: "rgba(56,189,248,0.25)" },
  { key: "mkcl_era_marks",  label: "MKCL/ERA Marks",  subtitle: "Admin assigned (max 50)", color: "#f97316", bg: "rgba(249,115,22,0.08)",  border: "rgba(249,115,22,0.25)"  },
  { key: "engagement_marks",label: "Engagement Marks",subtitle: "Posts+Likes+Comments+Views", color: "#a855f7", bg: "rgba(168,85,247,0.08)", border: "rgba(168,85,247,0.25)" },
];

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await apiClient.get(`/users/${userId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error("User not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  const fetchUserPosts = useCallback(async () => {
    try {
      const response = await apiClient.get("/posts", { params: { author_id: userId } });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Failed to load posts");
    }
  }, [userId]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await apiClient.get(`/users/${userId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics");
    }
  }, [userId]);

  const fetchScorecard = useCallback(async () => {
    try {
      const response = await apiClient.get(`/users/${userId}/scorecard`);
      setScorecard(response.data);
    } catch (error) {
      console.error("Failed to load scorecard");
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
    fetchUserPosts();
    fetchAnalytics();
    fetchScorecard();
  }, [fetchProfile, fetchUserPosts, fetchAnalytics, fetchScorecard]);

  const handleLikeToggle = async (postId, liked, likesCount) => {
    setPosts(posts.map(p => 
      p.id === postId 
        ? { ...p, liked_by_user: liked, likes_count: likesCount }
        : p
    ));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric'
    });
  };

  const formatDOB = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAvatarSrc = () => {
    if (profile?.avatar_url && profile.avatar_url.startsWith("/api/uploads/")) {
      return resolveMediaUrl(profile.avatar_url);
    }
    return profile?.avatar_url;
  };

  const escapeHtml = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  };

  const handleExportPortfolio = () => {
    if (!profile) return;

    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Please allow popups to export PDF");
      return;
    }

    const joined = profile.created_at ? formatDate(profile.created_at) : "-";
    const topTags = analytics?.top_tags?.length
      ? analytics.top_tags.map((item) => `#${escapeHtml(item.tag)} (${item.count})`).join(", ")
      : "None";

    const postItems = posts.map((post, idx) => {
      return `
        <div class="post-item">
          <h3>${idx + 1}. ${escapeHtml(post.title || "Untitled")}</h3>
          <p>${escapeHtml(post.description || "")}</p>
          <p class="meta">Likes: ${post.likes_count || 0} | Comments: ${post.comments_count || 0} | Created: ${escapeHtml(post.created_at || "-")}</p>
        </div>
      `;
    }).join("\n");

    const html = `
      <html>
        <head>
          <title>${escapeHtml(profile.full_name)} - Portfolio</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 32px; color: #111827; }
            h1 { margin: 0 0 8px; font-size: 28px; }
            h2 { margin: 24px 0 10px; font-size: 18px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
            h3 { margin: 0 0 6px; font-size: 16px; }
            p { margin: 0 0 8px; line-height: 1.5; }
            .subtle { color: #6b7280; font-size: 12px; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px 18px; }
            .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
            .post-item { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 10px; }
            .meta { color: #6b7280; font-size: 12px; }
            .skills span { display: inline-block; margin: 0 6px 6px 0; padding: 4px 8px; border-radius: 999px; background: #eef2ff; color: #3730a3; font-size: 12px; }
            @media print {
              body { margin: 18px; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(profile.full_name)}</h1>
          <p>${escapeHtml(profile.role || "user")} | Joined: ${escapeHtml(joined)}</p>
          <p class="subtle">Generated on ${new Date().toLocaleString()}</p>

          <h2>Profile Summary</h2>
          <div class="grid">
            <div><strong>Email:</strong> ${escapeHtml(profile.email || "-")}</div>
            <div><strong>Mobile:</strong> ${escapeHtml(profile.mobile_no || "-")}</div>
            <div><strong>Batch:</strong> ${escapeHtml(profile.batch_name || "-")}</div>
            <div><strong>Course:</strong> ${escapeHtml(profile.course_name || "-")}</div>
            <div><strong>Date of Birth:</strong> ${escapeHtml(formatDOB(profile.date_of_birth) || "-")}</div>
            <div><strong>Address:</strong> ${escapeHtml(profile.home_address || "-")}</div>
          </div>

          <h2>Bio</h2>
          <p>${escapeHtml(profile.bio || "No bio added.")}</p>

          <h2>Skills</h2>
          <div class="skills">
            ${profile.skills?.length ? profile.skills.map((skill) => `<span>${escapeHtml(skill)}</span>`).join("") : "<p>No skills added.</p>"}
          </div>

          <h2>Analytics</h2>
          <div class="grid">
            <div class="card"><strong>Total Posts</strong><p>${analytics?.total_posts ?? posts.length}</p></div>
            <div class="card"><strong>Total Likes Received</strong><p>${analytics?.total_likes_received ?? 0}</p></div>
            <div class="card"><strong>Total Comments Received</strong><p>${analytics?.total_comments_received ?? 0}</p></div>
            <div class="card"><strong>Avg Likes/Post</strong><p>${analytics?.avg_likes_per_post ?? 0}</p></div>
            <div class="card"><strong>Avg Comments/Post</strong><p>${analytics?.avg_comments_per_post ?? 0}</p></div>
            <div class="card"><strong>Top Tags</strong><p>${topTags}</p></div>
          </div>

          <div class="page-break"></div>
          <h2>Project Posts (${posts.length})</h2>
          ${postItems || "<p>No posts yet.</p>"}

          <script>
            window.onload = function () {
              setTimeout(function () { window.print(); }, 300);
            };
          </script>
        </body>
      </html>
    `;

    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const isOwnProfile = currentUser.id === userId;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold truncate flex-1 min-w-0">{profile.full_name}</h1>
          <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-end gap-2">
            <Button
              onClick={handleExportPortfolio}
              variant="outline"
              className="rounded-full px-3 sm:px-4"
              data-testid="export-portfolio-pdf-button"
            >
              <DownloadSimple className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
            {isOwnProfile && (
              <Link
                to="/settings"
                className="p-2 rounded-lg hover:bg-surface transition-colors"
                data-testid="edit-profile-button"
              >
                <PencilSimple className="w-5 h-5" />
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Profile Card */}
        <div 
          className="bg-surface rounded-xl border border-border/40 overflow-hidden mb-6"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1767477665589-3e729f5909f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjeWJlcnB1bmslMjBhYnN0cmFjdCUyMGNvZGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NDM3OTM5NXww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="bg-gradient-to-t from-surface via-surface/95 to-surface/60 p-6 pt-24">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4">
              {/* Avatar */}
              {profile.avatar_url ? (
                <img
                  src={getAvatarSrc()}
                  alt={profile.full_name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-surface -mt-12 sm:mt-0"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-4xl font-bold text-background border-4 border-surface -mt-12 sm:mt-0">
                  {profile.full_name?.charAt(0).toUpperCase()}
                </div>
              )}
              
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-heading text-2xl font-bold">{profile.full_name}</h2>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground mt-1">
                  {profile.role === "student" ? (
                    <GraduationCap className="w-4 h-4" />
                  ) : (
                    <Chalkboard className="w-4 h-4" />
                  )}
                  <span className="capitalize">{profile.role}</span>
                  <span>•</span>
                  <Calendar className="w-4 h-4" />
                  <span>Joined {formatDate(profile.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            {profile.bio && (
              <p className="mt-4 text-foreground/90 leading-relaxed">
                {profile.bio}
              </p>
            )}

            {/* Skills */}
            {profile.skills?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {profile.skills.map(skill => (
                  <span
                    key={skill}
                    className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            )}

            {/* Stats */}
            <div className="mt-6 flex items-center justify-center sm:justify-start gap-6">
              <div className="text-center sm:text-left">
                <div className="font-heading text-2xl font-bold">{profile.post_count || 0}</div>
                <div className="text-sm text-muted-foreground">Posts</div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details Card */}
        {(profile.email || profile.mobile_no || profile.date_of_birth || profile.home_address || profile.batch_name || profile.course_name) && (
          <div className="bg-surface rounded-xl border border-border/40 p-6 mb-6">
            <h3 className="font-heading font-semibold text-lg mb-4">Profile Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {profile.email && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Envelope className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium">{profile.email}</p>
                  </div>
                </div>
              )}
              
              {profile.mobile_no && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Mobile</p>
                    <p className="font-medium">{profile.mobile_no}</p>
                  </div>
                </div>
              )}
              
              {profile.date_of_birth && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Date of Birth</p>
                    <p className="font-medium">{formatDOB(profile.date_of_birth)}</p>
                  </div>
                </div>
              )}
              
              {profile.batch_name && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-danger/10 flex items-center justify-center">
                    <Student className="w-5 h-5 text-danger" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Batch</p>
                    <p className="font-medium">{profile.batch_name}</p>
                  </div>
                </div>
              )}
              
              {profile.course_name && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Books className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Course</p>
                    <p className="font-medium">{profile.course_name}</p>
                  </div>
                </div>
              )}
              
              {profile.home_address && (
                <div className="flex items-start gap-3 sm:col-span-2">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Address</p>
                    <p className="font-medium">{profile.home_address}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scorecard */}
        {scorecard && (
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <h3 className="font-heading font-semibold text-lg">Score Card</h3>
            </div>
            <div className="bg-surface rounded-xl border border-border/40 p-5">
              {/* 4 mark type cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {SCORE_TYPES.map(({ key, label, subtitle, color, bg, border }) => (
                  <div
                    key={key}
                    className="rounded-xl p-4 border flex flex-col gap-1"
                    style={{ background: bg, borderColor: border }}
                  >
                    <p className="text-xs font-medium" style={{ color, opacity: 0.85 }}>{label}</p>
                    <p
                      className="font-heading text-2xl font-bold"
                      style={{ color }}
                    >
                      {scorecard.scorecard?.[key] ?? 0}
                    </p>
                    <p className="text-xs" style={{ color, opacity: 0.6 }}>{subtitle}</p>
                  </div>
                ))}
              </div>
              {/* Total bar */}
              <div
                className="rounded-xl px-5 py-4 border flex items-center justify-between"
                style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.25)" }}
              >
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5" style={{ color: "#dc2626" }} />
                  <span className="font-semibold" style={{ color: "#dc2626" }}>Total Score</span>
                </div>
                <span
                  className="font-heading text-3xl font-bold"
                  style={{ color: "#dc2626" }}
                >
                  {scorecard.scorecard?.total_marks ?? 0}
                </span>
              </div>
              {/* Quick stats row */}
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-muted-foreground">
                <div>📣 {scorecard.stats?.total_referrals ?? 0} Referrals</div>
                <div>📝 {scorecard.stats?.total_posts ?? 0} Posts</div>
                <div>❤️ {scorecard.stats?.total_likes_received ?? 0} Likes</div>
                <div>👁 {scorecard.stats?.total_views ?? 0} Views</div>
              </div>
            </div>
          </section>
        )}

        {/* Post Analytics */}
        {analytics && (
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Article className="w-5 h-5 text-accent" />
              <h3 className="font-heading font-semibold text-lg">Post Analytics</h3>
            </div>

            <div className="bg-surface rounded-xl border border-border/40 p-5 mb-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Total Posts</p>
                  <p className="font-heading text-2xl font-bold">{analytics.total_posts}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Likes Received</p>
                  <p className="font-heading text-2xl font-bold">{analytics.total_likes_received}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Comments Received</p>
                  <p className="font-heading text-2xl font-bold">{analytics.total_comments_received}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Avg Likes/Post</p>
                  <p className="font-heading text-2xl font-bold">{analytics.avg_likes_per_post}</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-surface rounded-xl border border-border/40 p-4">
                <h4 className="font-semibold mb-2">Content Mix</h4>
                <p className="text-sm text-muted-foreground">Code: {analytics.content_mix?.code_posts || 0}</p>
                <p className="text-sm text-muted-foreground">Media: {analytics.content_mix?.media_posts || 0}</p>
                <p className="text-sm text-muted-foreground">Text Only: {analytics.content_mix?.text_only_posts || 0}</p>
              </div>

              <div className="bg-surface rounded-xl border border-border/40 p-4">
                <h4 className="font-semibold mb-2">Top Tags</h4>
                {analytics.top_tags?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {analytics.top_tags.map((item) => (
                      <span key={item.tag} className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                        #{item.tag} ({item.count})
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No tags yet</p>
                )}
              </div>

              <div className="bg-surface rounded-xl border border-border/40 p-4">
                <h4 className="font-semibold mb-2">Top Performing</h4>
                <p className="text-sm text-muted-foreground">
                  Most liked: {analytics.most_liked_post?.title || "-"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Most commented: {analytics.most_commented_post?.title || "-"}
                </p>
              </div>
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center gap-2 mb-4">
            <Article className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold text-lg">
              {isOwnProfile ? "Your Posts" : `Posts by ${profile.full_name.split(" ")[0]}`}
            </h3>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-xl border border-border/40">
              <Code className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                {isOwnProfile 
                  ? "You haven't posted anything yet" 
                  : "No posts yet"}
              </p>
              {isOwnProfile && (
                <Link to="/">
                  <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                    Create your first post
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map(post => (
                <PostCard 
                  key={post.id} 
                  post={post} 
                  onLikeToggle={handleLikeToggle}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
